import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ComposedChart,
  ReferenceLine,
} from "recharts";

// Create a local type that includes the backend id
interface MultiScaleDataPointWithId {
  id: number;
  date: string;
  subscales: { name: string; score: number; interpretation: string }[];
  responder: string; // Add responder
}

interface MultiScaleLongitudinalChartProps {
  data: MultiScaleDataPointWithId[];
  measureName: string;
  onPointClick?: (id: number) => void;
  selectedIds?: number[];
  initialBaseline?: {
    date: string;
    responder: string;
    scores: Record<string, number>;
  } | null;
}

export function MultiScaleLongitudinalChart({
  data,
  // measureName: _measureName,
  onPointClick,
  selectedIds = [],
  initialBaseline = null,
}: MultiScaleLongitudinalChartProps & { measureName?: string }) {
  // Get unique subscale names
  const subscaleNames = data[0]?.subscales.map((s) => s.name) || [];

  const THRESHOLD_SUBSCALES = new Set([
    "Predominantly Inattentive",
    "Predominantly Hyperactive/Impulsive",
    "Average Performance Score",
    "Total Score", // PHQ-9 and GAD-7
  ]);

  const getThreshold = (subscaleName: string) => {
    if (!initialBaseline || !THRESHOLD_SUBSCALES.has(subscaleName))
      return undefined;
    const score = initialBaseline.scores[subscaleName];
    if (score === undefined) return undefined;

    const label = `Baseline (${initialBaseline.date}${initialBaseline.responder ? ", " + initialBaseline.responder : ""})`;
    return { value: score, label };
  };

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Record<string, unknown>[];
  }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload as {
        id: number;
        date: string;
        responder?: string;
        [key: string]: unknown;
      };
      const dataPointId = dataPoint.id;
      const isSelected = selectedIds.includes(dataPointId);
      const selectionIndex = selectedIds.indexOf(dataPointId);

      let selectionLabel = "";
      if (isSelected) {
        selectionLabel = selectionIndex === 0 ? " (Primary)" : " (Comparison)";
      }

      return (
        <div
          className="bg-white p-3 rounded-lg shadow-lg border"
          style={{ borderColor: "#E2E8F0" }}
        >
          <p className="font-bold" style={{ color: "#1A202C" }}>
            {dataPoint.date}{" "}
            {dataPoint.responder ? `- ${dataPoint.responder}` : null}
            {selectionLabel && (
              <span
                className="ml-1"
                style={{ color: selectionIndex === 0 ? "#F59E0B" : "#8B5CF6" }}
              >
                {selectionLabel}
              </span>
            )}
          </p>
          <p className="text-sm" style={{ color: "#5A8DAB" }}>
            {String(payload[0].name)}:{" "}
            {String(dataPoint[payload[0].dataKey as string])}
          </p>
          <p className="text-xs mt-1" style={{ color: "#5A8DAB" }}>
            Click to select/compare
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom dot for clickable points
  const CustomDot = (subscaleName: string, isForActiveDot = false) => {
    const DotRenderer = (props: {
      cx?: number;
      cy?: number;
      payload?: Record<string, unknown>;
    }) => {
      const { cx, cy, payload } = props;
      const dataPointId = (payload as Record<string, unknown>)?.id as number;
      const isSelected = selectedIds.includes(dataPointId);
      const selectionIndex = selectedIds.indexOf(dataPointId);

      return (
        <g
          style={{ cursor: "pointer" }}
          onClick={() => onPointClick?.(dataPointId)}
        >
          {/* Larger invisible circle for easier clicking */}
          <circle
            cx={cx}
            cy={cy}
            r={isForActiveDot ? 20 : 15}
            fill="transparent"
            stroke="transparent"
          />
          {/* Visible dot */}
          {/* Visible dot */}
          <circle
            cx={cx}
            cy={cy}
            r={isSelected ? (isForActiveDot ? 10 : 8) : isForActiveDot ? 7 : 5}
            fill={
              isSelected
                ? selectionIndex === 0
                  ? "#F59E0B"
                  : "#8B5CF6"
                : subscaleName === subscaleNames[0]
                  ? "#5A8DAB"
                  : "#10B981"
            }
            stroke={
              isSelected
                ? selectionIndex === 0
                  ? "#F59E0B"
                  : "#8B5CF6"
                : subscaleName === subscaleNames[0]
                  ? "#5A8DAB"
                  : "#10B981"
            }
            strokeWidth={isSelected ? 3 : 2}
          />
          {isSelected && (
            <text
              x={cx as number}
              y={(cy as number) - 12}
              textAnchor="middle"
              fill={selectionIndex === 0 ? "#F59E0B" : "#8B5CF6"}
              fontSize="10px"
              fontWeight="bold"
            >
              {selectionIndex + 1}
            </text>
          )}
        </g>
      );
    };
    DotRenderer.displayName = `CustomDot(${subscaleName})`;
    return DotRenderer;
  };

  // Transform data for each subscale chart
  const getChartData = (subscaleIndex: number) => {
    return data.map((point) => ({
      id: point.id, // Pass id through
      date: point.date,
      xKey: `${point.date}_${point.id}`, // Unique key for XAxis to avoid merging points on same date
      score: point.subscales[subscaleIndex].score,
      interpretation: point.subscales[subscaleIndex].interpretation,
      responder: point.responder,
    }));
  };

  const selectedPoints = selectedIds
    .map((id) => data.find((d) => d.id === id))
    .filter((p): p is MultiScaleDataPointWithId => Boolean(p));

  return (
    <div className="w-full">
      {selectedPoints.length > 0 && (
        <div className="flex justify-end mb-4 gap-4">
          {selectedPoints.map((point, idx) => (
            <div key={point.id} className="text-sm font-medium">
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: idx === 0 ? "#F59E0B" : "#8B5CF6" }}
              ></span>
              {idx === 0 ? "Primary" : "Comparison"}: {point.date}{" "}
              {point.responder && `- ${String(point.responder)}`}
            </div>
          ))}
        </div>
      )}

      {/* Two charts side by side */}
      <div className="grid grid-cols-2 gap-6">
        {subscaleNames.map((subscaleName, index) => {
          const chartData = getChartData(index);
          const color = index === 0 ? "#5A8DAB" : "#10B981";

          return (
            <div key={subscaleName} className="flex flex-col">
              <h4
                className="text-sm font-semibold mb-2"
                style={{ color: "#1A202C" }}
              >
                {subscaleName}
              </h4>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                  <XAxis
                    dataKey="xKey"
                    stroke="#2D3748"
                    style={{ fontSize: "11px" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(val) => val.split("_")[0]}
                  />
                  <YAxis
                    stroke="#2D3748"
                    style={{ fontSize: "11px" }}
                    domain={[
                      0,
                      (dataMax: number) => {
                        const threshold = getThreshold(subscaleName);
                        const maxVal =
                          threshold && threshold.value > dataMax
                            ? threshold.value
                            : dataMax;
                        return Math.max(maxVal + 0.5, 3);
                      },
                    ]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {getThreshold(subscaleName) && (
                    <ReferenceLine
                      y={getThreshold(subscaleName)?.value}
                      label={{
                        value: getThreshold(subscaleName)?.label,
                        position: "insideBottomRight",
                        fill: "#EF4444",
                        fontSize: 10,
                        fontWeight: "bold",
                      }}
                      stroke="#EF4444"
                      strokeDasharray="3 3"
                    />
                  )}
                  <Line
                    key={subscaleName}
                    type="monotone"
                    dataKey="score"
                    stroke={color}
                    strokeWidth={2}
                    dot={CustomDot(subscaleName)}
                    activeDot={CustomDot(subscaleName, true)}
                    name={subscaleName}
                    connectNulls={true}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          );
        })}
      </div>
    </div>
  );
}
